import Markdown from 'markdown-to-jsx';
import styles from './styles.module.scss';
import React, { useState, useCallback } from 'react';
import { IconID } from 'packages/radiant-react/icon';
import { Button } from 'packages/radiant-react/button';
import { Typography } from 'packages/radiant-react/typography';
import { Horizontal, Vertical } from 'packages/radiant-react/layout';
import { JSON_RESPONSE_LABEL, COPY_RESET_DELAY } from 'components/AiMode/constants';

function CodeBlock({ children, ...props }: React.HTMLAttributes<HTMLPreElement>) {
  const [copied, setCopied] = useState(false);
  const preRef = React.useRef<HTMLPreElement>(null);

  const handleCopy = useCallback(() => {
    const text = preRef.current?.textContent ?? '';
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), COPY_RESET_DELAY);
      })
      .catch((error) => {
        console.error('Failed to copy code to clipboard:', error);
      });
  }, []);

  return (
    <Vertical {...props}>
      <Vertical className={styles['code-container']}>
        <Horizontal className={styles['code-header']} vAlignContent="center">
          <Typography variant="p" className={styles['code-label']}>
            {JSON_RESPONSE_LABEL}
          </Typography>
          <Button
            type="secondary"
            size="s"
            icon={copied ? IconID.TICK_SELECTION_BLUE : IconID.COPY}
            onClick={handleCopy}
            className={styles['code-copy-button']}
          />
        </Horizontal>
        <pre ref={preRef} className={styles.pre}>
          {children}
        </pre>
      </Vertical>
    </Vertical>
  );
}

const MARKDOWN_OPTIONS = {
  overrides: {
    h1: { props: { className: styles.h1 } },
    h2: { props: { className: styles.h2 } },
    h3: { props: { className: styles.h3 } },
    h4: { props: { className: styles.h4 } },
    p: { props: { className: styles.p } },
    ul: { props: { className: styles.ul } },
    ol: { props: { className: styles.ol } },
    li: { props: { className: styles.li } },
    code: { props: { className: styles.code } },
    pre: { component: CodeBlock },
    a: {
      props: {
        className: styles.link,
        target: '_blank',
        rel: 'noopener noreferrer',
      },
    },
    strong: { props: { className: styles.strong } },
    em: { props: { className: styles.em } },
    hr: { props: { className: styles.hr } },
  },
};

function MarkdownRendererInner({ content }: { content: string }) {
  return (
    <Markdown className={styles.markdown} options={MARKDOWN_OPTIONS}>
      {content}
    </Markdown>
  );
}

export const MarkdownRenderer = React.memo(MarkdownRendererInner);
